import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Building, FileText, Clock, User, Trash2, Shield, ShieldOff } from 'lucide-react';
import { TextField } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Button, Badge } from '@atoms';
import { trpc } from '@lib/trpc';
import { SupplierRecord, InvoiceStatus } from '@types';
import { useAuth } from '@contexts/AuthContext';
import { usePermission, SUPPLIER_PERMISSIONS } from '@hooks/usePermissions';
import { useABNValidation } from '@hooks/useABNValidation';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { muiTheme } from '@/theme/muiTheme';


export const SupplierEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useAuth();
  const [formData, setFormData] = useState<Partial<SupplierRecord>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ABN validation (exclude current supplier when checking uniqueness)
  const { abnError, isValidating: isValidatingABN, validateABN } = useABNValidation(formData.abn || '', id);

  // Fetch supplier data
  const { data: supplierResponse, isLoading: isLoadingSupplier } = trpc.supplier.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  // Fetch all invoices to filter by supplier
  const { data: invoicesResponse, isLoading: isLoadingInvoices } = trpc.invoice.getAll.useQuery();

  // Fetch audit logs
  const { data: auditData, isLoading: isLoadingAudit } = trpc.audit.getByEntity.useQuery({
    entityType: 'supplier',
    entityId: id!,
    limit: 20,
    offset: 0,
  }, { enabled: !!id });

  // Update mutation
  const updateSupplierMutation = trpc.supplier.update.useMutation({
    onSuccess: () => {
      toast.success('Supplier updated successfully');
      navigate('/suppliers');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update supplier');
    },
  });

  // Verification update mutation
  const updateVerificationMutation = trpc.supplier.updateVerification.useMutation({
    onSuccess: (data) => {
      if (data?.data) {
        toast.success(`Supplier ${data.data.isVerified ? 'verified' : 'unverified'} successfully`);
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update verification status');
    },
  });

  // Delete mutation
  const deleteSupplierMutation = trpc.supplier.delete.useMutation({
    onSuccess: () => {
      toast.success('Supplier deleted successfully');
      navigate('/suppliers');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete supplier');
    },
  });

  // Check if user has permission to delete suppliers
  const { hasPermission: canDeleteSupplier } = usePermission(SUPPLIER_PERMISSIONS.DELETE);

  // Initialize form data when supplier is loaded
  useEffect(() => {
    if (supplierResponse?.data) {
      const supplier = supplierResponse.data;
      setFormData({
        name: supplier.name,
        abn: supplier.abn || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        terms: supplier.terms || '',
        taxNumber: supplier.taxNumber || '',
        isVerified: supplier.isVerified,
      });
    }
  }, [supplierResponse]);

  const handleInputChange = (field: keyof SupplierRecord, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!id) return;
    
    // Validate ABN if provided
    if (formData.abn?.trim()) {
      const isABNValid = await validateABN(formData.abn.trim());
      if (!isABNValid) {
        toast.error(abnError || 'ABN validation failed');
        return;
      }
    }
    
    setIsLoading(true);
    try {
      await updateSupplierMutation.mutateAsync({
        id,
        data: {
          name: formData.name,
          abn: formData.abn,
          phone: formData.phone,
          email: formData.email,
          terms: formData.terms,
          taxNumber: formData.taxNumber,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/suppliers');
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    
    try {
      await deleteSupplierMutation.mutateAsync({ id });
    } catch (error) {
      // Error is handled by the mutation's onError callback
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleVerificationToggle = async () => {
    if (!id) return;

    const newVerificationStatus = !(formData.isVerified || false);

    // Update local state immediately for instant UI feedback
    setFormData(prev => ({
      ...prev,
      isVerified: newVerificationStatus,
    }));

    try {
      await updateVerificationMutation.mutateAsync({
        id,
        isVerified: newVerificationStatus,
      });
    } catch (error) {
      // Revert the state change on error
      setFormData(prev => ({
        ...prev,
        isVerified: !newVerificationStatus,
      }));
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('en-AU');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num).replace('A$', '$');
  };

  const getStatusBadge = (status: InvoiceStatus | undefined) => {
    const safeStatus = status || 'unprocessed';
    const statusConfig = {
      unprocessed: { variant: 'default' as const, label: 'Unprocessed' },
      processed: { variant: 'info' as const, label: 'Processed' },
      step3: { variant: 'warning' as const, label: 'Step 3' },
      step4: { variant: 'warning' as const, label: 'Step 4' },
      completed: { variant: 'success' as const, label: 'Completed' },
    };

    const config = statusConfig[safeStatus] || statusConfig.unprocessed;
    
    return (
      <Badge variant={config.variant} size="sm">
        {config.label}
      </Badge>
    );
  };

  const auditLogs = auditData?.logs || [];

  if (isLoadingSupplier) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!supplierResponse?.data) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-secondary-800 mb-2">Supplier not found</h2>
          <p className="text-secondary-600 mb-4">The supplier you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/suppliers')}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Suppliers
          </button>
        </div>
      </div>
    );
  }

  const supplier = supplierResponse.data;

  // Filter invoices for this supplier and get last 20
  const supplierInvoices = invoicesResponse ? 
    invoicesResponse
      .filter(invoice => invoice.supplier === supplier.name)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 20) 
    : [];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-secondary-200 -mx-4 px-4 mb-8">
        <div className="py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button
                variant="secondary"
                size="sm"
                icon={ArrowLeft}
                onClick={handleCancel}
              >
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-secondary-900">
                  Edit Supplier
                </h1>
                <p className="text-sm text-secondary-600">
                  {supplier.name}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                size="md"
                icon={X}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              {/* Verification Toggle Button */}
              <Button
                variant={(formData.isVerified || false) ? "default" : "success"}
                size="md"
                icon={(formData.isVerified || false) ? ShieldOff : Shield}
                loading={updateVerificationMutation.isPending}
                onClick={handleVerificationToggle}
                className={(formData.isVerified || false)
                  ? "bg-orange-100 hover:bg-orange-200 text-orange-700 border-orange-300 border"
                  : "border border-green-600 hover:border-green-700"
                }
              >
                {(formData.isVerified || false) ? 'Unverify' : 'Verify'}
              </Button>
              {/* Delete button - only visible to users with delete permission */}
              {canDeleteSupplier && (
                <Button
                  variant="danger"
                  size="md"
                  icon={Trash2}
                  loading={deleteSupplierMutation.isPending}
                  onClick={handleDeleteClick}
                >
                  Delete
                </Button>
              )}
              <Button
                variant="primary"
                size="md"
                icon={Save}
                loading={isLoading}
                disabled={isValidatingABN || !!abnError}
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {/* Top Row - Form and Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-secondary-900 mb-6 flex items-center">
              <Building className="w-5 h-5 mr-2 text-primary-500" />
              Supplier Details
            </h2>
            
            <ThemeProvider theme={muiTheme}>
              <div className="space-y-6">
                {/* Explanation */}
                <div className="bg-primary-50 border border-primary-200 rounded-md p-4">
                  <h3 className="text-sm font-medium text-primary-800 mb-2">About Supplier Information</h3>
                  <p className="text-sm text-primary-700">
                    Update the supplier's contact information, business details, and payment terms.
                    These changes will be reflected across all related invoices and reports.
                  </p>
                </div>

                {/* Supplier Name */}
                <TextField
                  label="Supplier Name *"
                  variant="outlined"
                  size="small"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  fullWidth
                />

                {/* ABN */}
                <TextField
                  label="ABN"
                  variant="outlined"
                  size="small"
                  value={formData.abn || ''}
                  onChange={(e) => handleInputChange('abn', e.target.value)}
                  placeholder="e.g., 12345678901"
                  fullWidth
                  error={!!abnError}
                  helperText={abnError || "Australian Business Number (optional)"}
                  InputProps={{
                    endAdornment: isValidatingABN ? (
                      <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    ) : null,
                  }}
                />

                {/* Phone */}
                <TextField
                  label="Phone Number"
                  type="tel"
                  variant="outlined"
                  size="small"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="e.g., (07) 3123 4567"
                  fullWidth
                />

                {/* Email */}
                <TextField
                  label="Email Address"
                  type="email"
                  variant="outlined"
                  size="small"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="e.g., accounts@supplier.com.au"
                  fullWidth
                />

                {/* Payment Terms */}
                <TextField
                  label="Payment Terms"
                  variant="outlined"
                  size="small"
                  value={formData.terms || ''}
                  onChange={(e) => handleInputChange('terms', e.target.value)}
                  placeholder="e.g., NET 30, COD, 30 EOM"
                  fullWidth
                />
              </div>
            </ThemeProvider>
          </div>

          {/* Right Panel - Supplier Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-primary-500" />
              Supplier Statistics
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="text-sm text-secondary-500">Unprocessed Invoices</div>
                <div className="text-2xl font-bold text-secondary-900">
                  {supplier.unprocessed_invoices_count}
                </div>
              </div>
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="text-sm text-secondary-500">Last Invoice</div>
                <div className="text-lg font-semibold text-secondary-900">
                  {formatDate(supplier.last_invoice_date)}
                </div>
              </div>
              <div className={`rounded-lg p-4 ${(formData.isVerified || false) ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                <div className="text-sm text-secondary-500 mb-1">Verification Status</div>
                <div className="flex items-center">
                  {(formData.isVerified || false) ? (
                    <Shield className="w-5 h-5 text-green-600 mr-2" />
                  ) : (
                    <ShieldOff className="w-5 h-5 text-orange-600 mr-2" />
                  )}
                  <span className={`text-lg font-semibold ${(formData.isVerified || false) ? 'text-green-800' : 'text-orange-800'}`}>
                    {(formData.isVerified || false) ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
                <div className="text-xs text-secondary-600 mt-1">
                  {(formData.isVerified || false)
                    ? 'This supplier has been verified and can be used for invoice processing'
                    : 'This supplier needs verification before being used for critical operations'
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Invoices Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-secondary-200">
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-primary-500" />
              Last 20 Invoices
              {isLoadingInvoices && (
                <div className="ml-2 w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              )}
            </h3>
          </div>
          <div className="overflow-x-auto">
            {supplierInvoices.length === 0 ? (
              <div className="text-center py-8 text-secondary-500">
                {isLoadingInvoices ? 'Loading invoices...' : 'No invoices found for this supplier.'}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Invoice Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Invoice Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Total Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {supplierInvoices.map((invoice) => (
                    <tr 
                      key={invoice.id} 
                      className="hover:bg-secondary-50 cursor-pointer transition-colors duration-150"
                      onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-secondary-900">
                          {invoice.invoice_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-secondary-900">
                          {format(new Date(invoice.invoice_date), 'dd/MM/yyyy')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge((invoice.status || 'unprocessed') as InvoiceStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-secondary-900">
                          {formatCurrency(invoice.total_amount)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Audit Log Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-secondary-200">
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-primary-500" />
              Audit Log
              {auditLogs.length > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-secondary-100 text-secondary-600 rounded-full">
                  {auditLogs.length}
                </span>
              )}
              {isLoadingAudit && (
                <div className="ml-2 w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              )}
            </h3>
          </div>
          <div className="p-6">
            {isLoadingAudit ? (
              <div className="text-center py-6">
                <div className="w-6 h-6 border-2 border-accent-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-secondary-500">Loading activity...</p>
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-6">
                <Clock className="w-8 h-8 text-secondary-300 mx-auto mb-2" />
                <p className="text-sm text-secondary-500">No activity recorded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log) => {
                  const actionIcons = {
                    create: <FileText className="w-4 h-4 text-green-500" />,
                    update: <Building className="w-4 h-4 text-blue-500" />,
                    status_change: <ArrowLeft className="w-4 h-4 text-orange-500" />,
                    delete: <X className="w-4 h-4 text-red-500" />,
                    view: <User className="w-4 h-4 text-gray-500" />,
                    export: <Save className="w-4 h-4 text-purple-500" />,
                  };

                  const actionColors = {
                    create: 'bg-green-50 border-green-200',
                    update: 'bg-blue-50 border-blue-200',
                    status_change: 'bg-orange-50 border-orange-200',
                    delete: 'bg-red-50 border-red-200',
                    view: 'bg-gray-50 border-gray-200',
                    export: 'bg-purple-50 border-purple-200',
                  };

                  const formatActionText = (log: any): string => {
                    switch (log.action) {
                      case 'create':
                        return 'Created';
                      case 'update':
                        if (log.changes?.fields && log.changes.fields.length > 0) {
                          if (log.changes.fields.length === 1) {
                            const field = log.changes.fields[0];
                            const beforeValue = log.changes.before?.[field];
                            const afterValue = log.changes.after?.[field];
                            if (beforeValue !== undefined && afterValue !== undefined) {
                              return `Updated ${field}: "${beforeValue || 'empty'}" → "${afterValue || 'empty'}"`;
                            }
                            return `Updated ${field}`;
                          } else {
                            const fieldNames = log.changes.fields.join(', ');
                            return `Updated ${log.changes.fields.length} fields: ${fieldNames}`;
                          }
                        }
                        return 'Updated';
                      case 'status_change':
                        if (log.changes?.before?.status && log.changes?.after?.status) {
                          return `Status changed: ${log.changes.before.status} → ${log.changes.after.status}`;
                        }
                        return 'Status changed';
                      case 'delete':
                        return 'Deleted';
                      case 'view':
                        return 'Viewed';
                      case 'export':
                        return 'Exported';
                      default:
                        return 'Unknown action';
                    }
                  };

                  const getUserDisplayName = (log: any): string => {
                    if (log.user?.name) return log.user.name;
                    if (log.user?.email) return log.user.email;
                    if (log.user_email) return log.user_email;
                    return 'System';
                  };

                  return (
                    <div
                      key={log.id}
                      className={`flex p-3 rounded-md border ${actionColors[log.action as keyof typeof actionColors] || 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex-shrink-0 mr-3">
                        {actionIcons[log.action as keyof typeof actionIcons] || <Clock className="w-4 h-4 text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-secondary-900">
                              {formatActionText(log)}
                            </p>
                            {log.action === 'update' && log.changes?.fields && log.changes.fields.length > 1 && (
                              <div className="mt-2">
                                <p className="text-xs text-secondary-600 font-medium mb-1">Changes:</p>
                                <div className="space-y-1">
                                  {log.changes.fields.map((field: string) => {
                                    const beforeValue = log.changes?.before?.[field];
                                    const afterValue = log.changes?.after?.[field];
                                    return (
                                      <div key={field} className="text-xs text-secondary-600 pl-2">
                                        <span className="font-medium">{field}:</span>
                                        <span className="ml-1">"</span>
                                        <span className="text-red-600">{beforeValue || 'empty'}</span>
                                        <span className="mx-1">" → "</span>
                                        <span className="text-green-600">{afterValue || 'empty'}</span>
                                        <span>"</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {log.metadata?.source && (
                              <p className="text-xs text-secondary-500 mt-1">
                                Source: {log.metadata.source}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-secondary-500 ml-2 flex-shrink-0">
                            {format(new Date(log.created_at), 'MMM d, yyyy HH:mm')}
                          </span>
                        </div>
                        <div className="flex items-center mt-2">
                          <User className="w-3 h-3 text-secondary-400 mr-1" />
                          <span className="text-xs text-secondary-600">{getUserDisplayName(log)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Supplier</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete supplier <strong>{supplier.name}</strong>?
                </p>
                {supplierInvoices.length > 0 && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> This supplier has {supplierInvoices.length} associated invoice(s). 
                      Deleting the supplier may affect these invoices.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleteSupplierMutation.isPending}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteSupplierMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleteSupplierMutation.isPending ? 'Deleting...' : 'Delete Supplier'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
